import { useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { absoluteUrl } from "../api/client";
import Modal from "./Modal";
import { FULL_CROP, cropOf, frameAspect } from "./geometry";

// Smallest crop, as a share of the image's width or height; as MIN_CROP in the API.
const MIN_CROP = 0.02;
const CORNERS = ["nw", "ne", "sw", "se"];
const EDGES = ["n", "e", "s", "w"];

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
// Three decimals, as the API keeps them.
const round = (value) => Math.round(value * 1000) / 1000;

// Largest crop of the given pixel shape that fits the image, centred on `centre`.
function fitAspect(shape, natural, centre) {
  const k = (shape * natural.height) / natural.width;
  const w = Math.min(1, k);
  const h = w / k;
  return {
    x: clamp(centre.x - w / 2, 0, 1 - w),
    y: clamp(centre.y - h / 2, 0, 1 - h),
    w,
    h,
  };
}

// A new frame with the crop's shape, about the same size and centred where the
// old one was, kept on the board.
export function frameForCrop(item, ratio, shape) {
  const area = item.w * (item.h / ratio);
  let w = Math.sqrt(area * shape);
  let h = Math.sqrt(area / shape) * ratio;
  const shrink = Math.min(1, 100 / w, 100 / h);
  // A very thin crop still gets a frame the API accepts (at least 1% each way).
  w = Math.max(1, w * shrink);
  h = Math.max(1, h * shrink);
  const cx = item.x + item.w / 2;
  const cy = item.y + item.h / 2;
  const tidy = (value) => Math.round(value * 1000) / 1000;
  return {
    x: tidy(clamp(cx - w / 2, 0, 100 - w)),
    y: tidy(clamp(cy - h / 2, 0, 100 - h)),
    w: tidy(w),
    h: tidy(h),
  };
}

export default function CropDialog({ item, asset, ratio, onApply, onClose }) {
  const [crop, setCrop] = useState(() => ({ ...cropOf(item) }));
  const [aspect, setAspect] = useState("free");
  const [reshape, setReshape] = useState(true);
  const [natural, setNatural] = useState(
    asset?.width && asset?.height ? { width: asset.width, height: asset.height } : null
  );
  const stageRef = useRef(null);
  const drag = useRef(null);

  const choices = [
    { key: "free", label: "Free" },
    { key: "frame", label: "Frame", shape: frameAspect(item, ratio) },
    { key: "original", label: "Original", shape: natural ? natural.width / natural.height : null },
    { key: "1:1", label: "1:1", shape: 1 },
    { key: "4:3", label: "4:3", shape: 4 / 3 },
    { key: "3:4", label: "3:4", shape: 3 / 4 },
    { key: "16:9", label: "16:9", shape: 16 / 9 },
  ];
  const locked = choices.find((choice) => choice.key === aspect)?.shape || null;

  const chooseAspect = (key) => {
    setAspect(key);
    const shape = choices.find((choice) => choice.key === key)?.shape;
    if (shape && natural) setCrop((current) => fitAspect(shape, natural, { x: current.x + current.w / 2, y: current.y + current.h / 2 }));
  };

  // The pointer as a share of the image; a flipped preview is mirrored back.
  const pointer = (event) => {
    const rect = stageRef.current.getBoundingClientRect();
    let u = (event.clientX - rect.left) / rect.width;
    let v = (event.clientY - rect.top) / rect.height;
    if (item.flip_x) u = 1 - u;
    if (item.flip_y) v = 1 - v;
    return { u, v };
  };

  const start = (event, handle) => {
    if (event.button !== 0 || !natural) return;
    event.stopPropagation();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { handle, from: pointer(event), origin: crop };
  };

  const move = (event) => {
    const d = drag.current;
    if (!d) return;
    const { u, v } = pointer(event);
    const o = d.origin;

    if (d.handle === "move") {
      setCrop({
        ...o,
        x: clamp(o.x + u - d.from.u, 0, 1 - o.w),
        y: clamp(o.y + v - d.from.v, 0, 1 - o.h),
      });
      return;
    }

    if (locked) {
      // Corners only: grow from the opposite corner, keeping the shape.
      const k = (locked * natural.height) / natural.width;
      const sx = d.handle.includes("e") ? 1 : -1;
      const sy = d.handle.includes("s") ? 1 : -1;
      const ax = sx > 0 ? o.x : o.x + o.w;
      const ay = sy > 0 ? o.y : o.y + o.h;
      let w = Math.max(MIN_CROP, sx * (u - ax), sy * (v - ay) * k, MIN_CROP * k);
      let h = w / k;
      const roomW = sx > 0 ? 1 - ax : ax;
      const roomH = sy > 0 ? 1 - ay : ay;
      if (w > roomW) [w, h] = [roomW, roomW / k];
      if (h > roomH) [w, h] = [roomH * k, roomH];
      setCrop({ x: sx > 0 ? ax : ax - w, y: sy > 0 ? ay : ay - h, w, h });
      return;
    }

    let left = o.x;
    let top = o.y;
    let right = o.x + o.w;
    let bottom = o.y + o.h;
    if (d.handle.includes("w")) left = clamp(u, 0, right - MIN_CROP);
    if (d.handle.includes("e")) right = clamp(u, left + MIN_CROP, 1);
    if (d.handle.includes("n")) top = clamp(v, 0, bottom - MIN_CROP);
    if (d.handle.includes("s")) bottom = clamp(v, top + MIN_CROP, 1);
    setCrop({ x: left, y: top, w: right - left, h: bottom - top });
  };

  const end = () => {
    drag.current = null;
  };

  const apply = () => {
    const x = round(crop.x);
    const y = round(crop.y);
    // Rounded separately, the crop could reach a hair past the image's edge.
    const tidy = { x, y, w: Math.min(round(crop.w), round(1 - x)), h: Math.min(round(crop.h), round(1 - y)) };
    const whole = tidy.x <= 0 && tidy.y <= 0 && tidy.w >= 1 && tidy.h >= 1;
    const patch = { crop: whole ? null : tidy, zoom: 1, focus_x: 50, focus_y: 50 };
    if (reshape && aspect !== "frame" && natural) {
      Object.assign(patch, frameForCrop(item, ratio, (tidy.w * natural.width) / (tidy.h * natural.height)));
    }
    onApply(patch);
  };

  const box = { left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` };
  const flip = `scale(${item.flip_x ? -1 : 1}, ${item.flip_y ? -1 : 1})`;
  const pixels = natural ? `${Math.round(crop.w * natural.width)} × ${Math.round(crop.h * natural.height)} px` : "";

  return (
    <Modal title="Crop image" onClose={onClose} wide>
      <div className="mb-crop">
        <div className="mb-crop-aspects" role="radiogroup" aria-label="Crop shape">
          {choices.map((choice) => (
            <button
              key={choice.key}
              type="button"
              role="radio"
              aria-checked={aspect === choice.key}
              className={aspect === choice.key ? "active" : ""}
              onClick={() => chooseAspect(choice.key)}
              disabled={choice.key !== "free" && !choice.shape}
              title={choice.key === "frame" ? "Match the frame on the board" : choice.key === "original" ? "The image's own shape" : undefined}
            >
              {choice.label}
            </button>
          ))}
        </div>

        <div className="mb-crop-area">
          <div
            ref={stageRef}
            className="mb-crop-stage"
            style={{ transform: item.flip_x || item.flip_y ? flip : undefined }}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
          >
            <img
              src={absoluteUrl(asset.url)}
              alt=""
              draggable={false}
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (!natural && naturalWidth && naturalHeight) setNatural({ width: naturalWidth, height: naturalHeight });
              }}
            />
            {natural && (
              <div className="mb-crop-shade" aria-hidden="true">
                <span style={box} />
              </div>
            )}
            {natural && (
              <div className="mb-crop-box" style={box} onPointerDown={(event) => start(event, "move")}>
                <span className="mb-crop-grid" aria-hidden="true" />
                {(locked ? CORNERS : [...CORNERS, ...EDGES]).map((handle) => (
                  <span
                    key={handle}
                    className={`mb-crop-handle is-${handle}`}
                    onPointerDown={(event) => start(event, handle)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mb-crop-foot">
          <span className="mb-muted">{pixels}</span>
          <button
            type="button"
            className="mb-text-button"
            onClick={() => {
              setAspect("free");
              setCrop({ ...FULL_CROP });
            }}
          >
            <RotateCcw size={14} strokeWidth={1.8} aria-hidden="true" />
            Whole image
          </button>
        </div>

        {aspect !== "frame" && (
          <label className="mb-toggle">
            <input type="checkbox" checked={reshape} onChange={(event) => setReshape(event.target.checked)} />
            <span>Reshape the frame to fit the crop</span>
          </label>
        )}

        <div className="mb-modal-actions">
          <button type="button" className="mb-tool" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="mb-primary" onClick={apply} disabled={!natural}>
            Apply crop
          </button>
        </div>
      </div>
    </Modal>
  );
}
